<script setup>
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import MapComponent from '@/components/map/MapComponent.vue'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import {
  DETAIL_ESG,
  DETAIL_LANDBOARD_MAP_FEATURE_LAYER,
  MAP_CENTER,
  MAP_EXTENT,
  MAP_MINZOOM,
  MAP_PROJECTION,
} from '@/constants'
import { createGeoJsonFeature } from '@/services/map/FeatureService'
import { transform } from '@/services/map/ProjectionService'
import { useDetailStore } from '@/stores/useDetailStore'
import { useLandBoardStore } from '@/stores/useLandBoardStore'

/**
 * Off-screen ESG maps for RED-210 QV Word export.
 *
 * Renders one MapComponent per DETAIL_ESG factor (8 total) using the same
 * layer stack and `landBoard.geometry` / `landBoard.bbox` overlay the Detail
 * page's `EsgItemComponent.vue` uses. Host (QuickValuationResult) keeps a
 * template ref so `captureAll()` can stream base64 PNGs to the backend
 * alongside the four location maps during /quick-valuation/{id}/export.
 *
 * Uses the same offscreen trick as QvHiddenMaps: `display:none` would stop
 * OpenLayers initialising its canvas, so we use `position:absolute` +
 * `visibility:hidden` + real 720×540 dimensions.
 */

const props = defineProps({
  ehrData: {
    type: Object,
    default: () => ({}),
  },
  ehrId: {
    type: [String, Number],
    default: null,
  },
})

const { findLayer } = useDetailMapHelpers()
const { landBoard } = storeToRefs(useLandBoardStore())
const { ehrGeometry } = storeToRefs(useDetailStore())

// ESG ids, order preserved from DETAIL_ESG (matches the .docx row order).
const esgIds = Object.keys(DETAIL_ESG)

// Template refs can't be created dynamically inside v-for with useTemplateRef;
// use a keyed ref object instead.
const mapRefs = ref({})
const setMapRef = (id, el) => {
  if (el) {
    mapRefs.value[id] = el
  } else {
    delete mapRefs.value[id]
  }
}

const baseExtent = transform('extent', MAP_EXTENT, 'EPSG:4326', MAP_PROJECTION)
const fallbackCenter = transform('fromLonLat', MAP_CENTER, MAP_PROJECTION)

// Per-ESG view + overlay. Computed from DETAIL_ESG so every map self-configures.
const mapConfigs = computed(() =>
  esgIds.map((id) => {
    const entry = DETAIL_ESG[id] || {}
    return {
      id,
      baseLayers: (entry.base_layers || []).map((layer) => findLayer(layer)),
      infoLayers: (entry.layers || []).map((layer) => findLayer(layer)),
      // Radon risk uses a shallower max zoom in EsgItemComponent.vue:78 — mirror it here.
      maxZoom: id === 'radon_risk' ? 13 : 15,
      featureInfo: entry.feature_info || null,
    }
  }),
)

// Shared overlay feature layer (clone per-ESG so each map gets its own features array).
const buildOverlay = () => {
  const geometry = landBoard.value?.geometry
    ? [landBoard.value.geometry]
    : Array.isArray(ehrGeometry.value)
      ? ehrGeometry.value
      : []

  if (!geometry.length) return null

  const layer = findLayer(DETAIL_LANDBOARD_MAP_FEATURE_LAYER)
  if (!layer) return null

  geometry.forEach((geom) => {
    const feature = createGeoJsonFeature(geom, { id: props.ehrId })
    if (feature) layer.features.push(feature)
  })
  return layer
}

const overlays = ref({}) // { [id]: layerClone }
const bbox = ref(null)

const applyLandBoardState = () => {
  // Build a fresh overlay per ESG id (findLayer returns a deep clone) so each
  // MapComponent can mutate its own features array independently.
  const next = {}
  for (const id of esgIds) {
    next[id] = buildOverlay()
  }
  overlays.value = next
  bbox.value = landBoard.value?.bbox ?? null
}

onMounted(applyLandBoardState)

// Re-apply if landBoard lands asynchronously (Inertia deferred, or parent re-sync).
watch(() => [landBoard.value, ehrGeometry.value], applyLandBoardState, { deep: false })

// Push bbox/overlay updates into already-mounted MapComponents (mirrors
// EsgItemComponent.vue:129-143).
watch(overlays, (next) => {
  for (const id of esgIds) {
    const ref = mapRefs.value[id]
    if (ref?.updateOverlay && next[id]) {
      ref.updateOverlay(next[id])
    }
  }
}, { deep: false })

watch(bbox, (next) => {
  if (!next) return
  for (const id of esgIds) {
    const ref = mapRefs.value[id]
    if (ref?.updateBbox) {
      ref.updateBbox(next)
    }
  }
})

const exportOne = async (id) => {
  const mapRef = mapRefs.value[id]
  if (!mapRef) return ''

  // MapComponent has no `overlay`/`bbox` prop watcher — it reads them once at
  // mount from the initial props. Call updateOverlay / updateBbox right before
  // capturing so the map re-fits to the property (otherwise we'd capture a
  // blank Estonia-wide viewport where WMS tiles haven't loaded).
  //
  // RED-210: No more arbitrary 350ms sleep — exportMap() now awaits nextTick
  // and rendercomplete, which handles the fit+tile-load window correctly.
  if (overlays.value[id] && mapRef.updateOverlay) {
    mapRef.updateOverlay(overlays.value[id])
  }
  if (bbox.value && mapRef.updateBbox) {
    mapRef.updateBbox(bbox.value)
  }

  if (!mapRef.exportMap) return ''
  try {
    // JPEG 0.85 — ESG backgrounds are photo/gradient imagery; lossless PNG is
    // wasted bytes. Saves ~70–85% per map vs. PNG, no visible degradation at
    // the 360pt (~480px) width they're embedded at in the .docx.
    return await mapRef.exportMap({ format: 'jpeg', quality: 0.85 })
  } catch (err) {
    console.warn('[QvHiddenEsgMaps] exportMap failed', id, err)
    return ''
  }
}

/**
 * Bounded-concurrency capture (RED-210).
 *
 * The historical sequential loop was in place because parallel Promise.all
 * saturated the WMS pipeline at _initial_ load and returned blanks. By the
 * time the user clicks Generate the hidden maps have been alive on the page
 * for seconds and tiles are cached, so we can safely run several in parallel.
 *
 * Concurrency=3 as a compromise: meaningful speed-up vs. 8-wide blast.
 * If a slot returns an empty string (exportMap's error sentinel) we retry it
 * once sequentially — same "never ship a blank ESG map" guarantee as before.
 */
const CAPTURE_CONCURRENCY = 3

const captureAll = async (onProgress) => {
  const result = {}
  const queue = [...esgIds]
  const total = esgIds.length
  let completed = 0
  const tick = (label) => {
    completed += 1
    if (typeof onProgress === 'function') {
      onProgress({ completed, total, label })
    }
  }

  const worker = async () => {
    while (queue.length > 0) {
      const id = queue.shift()
      if (id === undefined) break
      result[id] = await exportOne(id)
      tick(id)
    }
  }

  const workers = Array.from(
    { length: Math.min(CAPTURE_CONCURRENCY, esgIds.length) },
    worker,
  )
  await Promise.all(workers)

  // Retry any blanks sequentially — a blank here is usually a transient
  // tile-loss caused by the burst, not a real failure. Don't double-count
  // toward progress; the original tick already fired for this slot.
  for (const id of esgIds) {
    if (!result[id]) {
      result[id] = await exportOne(id)
    }
  }

  return result
}

defineExpose({ captureAll, total: esgIds.length })
</script>

<template>
  <div
    aria-hidden="true"
    class="qv-hidden-esg-maps"
    style="position: absolute; left: -10000px; top: -10000px; width: 720px; height: 540px; visibility: hidden; pointer-events: none;"
  >
    <div
      v-for="config in mapConfigs"
      :key="config.id"
      class="qv-esg-slot"
    >
      <MapComponent
        :ref="(el) => setMapRef(config.id, el)"
        :center="fallbackCenter"
        :extent="baseExtent"
        :zoom="3"
        :bbox="bbox"
        :min-zoom="MAP_MINZOOM"
        :max-zoom="config.maxZoom"
        :projection="MAP_PROJECTION"
        :base-layers="config.baseLayers"
        :info-layers="config.infoLayers"
        :overlay="overlays[config.id]"
        :zoom-to-features="true"
        :zoom-control="false"
      />
    </div>
  </div>
</template>

<style scoped>
/* 720×540 slot matches the original pre-RED-210 layout. MapComponent's
 * `min-width: 900px` forces each captured ESG canvas to 900×540 (5:3), same
 * as the Detail page's EsgItemComponent. Shrinking the slot dragged the
 * `fit()` zoom out enough that the landboard red-stroke overlay became too
 * small to see — restored here. Payload savings now come from JPEG 0.85
 * compression on these 8 maps plus the multipart upload. */
.qv-esg-slot {
  width: 720px;
  height: 540px;
  margin-bottom: 10px;
}
</style>
